import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Radio, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const sportEmoji = {
    'NBA': 'NBA', 'NCAAB': 'NCAAB', 'NHL': 'NHL', 'MLB': 'MLB',
    'NFL': 'NFL', 'EPL': 'EPL', 'La Liga': 'La Liga',
    'Serie A': 'Serie A', 'Bundesliga': 'Bund', 'MMA': 'MMA',
};

const LiveScoresTicker = () => {
    const [live, setLive] = useState([]);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);

    const fetchScores = async () => {
        try {
            const { data } = await axios.get(`${API}/sports/live/scores`, { withCredentials: true });
            setLive(data.live || []);
            setRecent(data.recent || []);
        } catch {}
        setLoading(false);
    };

    useEffect(() => {
        fetchScores();
        const interval = setInterval(fetchScores, 90000); // refresh every 90s
        return () => clearInterval(interval);
    }, []);

    const scroll = (dir) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: dir * 280, behavior: 'smooth' });
        }
    };

    const allGames = [...live, ...recent.slice(0, 10)];
    if (loading && allGames.length === 0) return null;
    if (allGames.length === 0) return null;

    return (
        <div className="relative mb-6" data-testid="live-scores-ticker">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    {live.length > 0 && (
                        <span className="flex items-center gap-1.5">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B30]" />
                            </span>
                            <span className="text-xs font-bold text-[#FF3B30] uppercase tracking-wider">Live</span>
                        </span>
                    )}
                    <span className="text-sm font-bold text-white">
                        Scores {live.length > 0 && `(${live.length} in progress)`}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => scroll(-1)} className="p-1 rounded hover:bg-white/10 text-[#A0AAB2]" data-testid="ticker-scroll-left">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => scroll(1)} className="p-1 rounded hover:bg-white/10 text-[#A0AAB2]" data-testid="ticker-scroll-right">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Scrollable cards */}
            <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1" style={{ scrollSnapType: 'x mandatory' }}>
                {allGames.map((game, i) => {
                    const isLive = !game.completed && game.home_score !== null;
                    return (
                        <motion.div
                            key={game.id + i}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.03 }}
                            className={`shrink-0 w-64 p-3 rounded-xl border transition-colors ${
                                isLive
                                    ? 'bg-[#0a0a0a] border-[#FF3B30]/30 shadow-[0_0_15px_rgba(255,59,48,0.1)]'
                                    : 'bg-[#0a0a0a] border-white/10'
                            }`}
                            style={{ scrollSnapAlign: 'start' }}
                            data-testid={`score-card-${game.id}`}
                        >
                            {/* Sport tag + status */}
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold text-[#A0AAB2] uppercase tracking-wider">
                                    {game.sport_title}
                                </span>
                                {isLive ? (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-[#FF3B30]">
                                        <Radio className="w-3 h-3 animate-pulse" /> LIVE
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-bold text-[#A0AAB2]">FINAL</span>
                                )}
                            </div>

                            {/* Teams + Scores */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm truncate max-w-[160px] ${
                                        game.completed && parseInt(game.away_score) > parseInt(game.home_score) ? 'text-white font-bold' : 'text-[#A0AAB2]'
                                    }`}>
                                        {game.away_team}
                                    </span>
                                    <span className={`text-lg font-mono font-black ${
                                        isLive ? 'text-white' : game.completed && parseInt(game.away_score) > parseInt(game.home_score) ? 'text-white' : 'text-[#A0AAB2]'
                                    }`}>
                                        {game.away_score ?? '-'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm truncate max-w-[160px] ${
                                        game.completed && parseInt(game.home_score) > parseInt(game.away_score) ? 'text-white font-bold' : 'text-[#A0AAB2]'
                                    }`}>
                                        {game.home_team}
                                    </span>
                                    <span className={`text-lg font-mono font-black ${
                                        isLive ? 'text-white' : game.completed && parseInt(game.home_score) > parseInt(game.away_score) ? 'text-white' : 'text-[#A0AAB2]'
                                    }`}>
                                        {game.home_score ?? '-'}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};

export default LiveScoresTicker;
